'use client';

import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Monitor, Headphones, FileText, X, Check } from 'lucide-react';
import { useT } from '@/hooks/useT';
import { usePriceConvert } from '@/lib/PriceContext';

// Назва мови в локалі сайту (English → Англійська для uk тощо); fallback — native.
export function langName(code: string, siteLang: string, fallback: string): string {
  try {
    const locale = siteLang === 'ru' ? 'ru' : siteLang === 'uk' ? 'uk' : 'en';
    const n = new Intl.DisplayNames([locale], { type: 'language' }).of(code.split('-')[0]);
    return n ? n.charAt(0).toUpperCase() + n.slice(1) : fallback;
  } catch { return fallback; }
}

export const LANG_LIST = [
  { code: 'uk-UA', flag: '🇺🇦', label: 'Українська' },
  { code: 'ru-RU', flag: '🇷🇺', label: 'Російська'  },
  { code: 'en-US', flag: '🇬🇧', label: 'English'    },
  { code: 'pl-PL', flag: '🇵🇱', label: 'Polski'     },
  { code: 'de-DE', flag: '🇩🇪', label: 'Deutsch'    },
  { code: 'fr-FR', flag: '🇫🇷', label: 'Français'   },
  { code: 'es-ES', flag: '🇪🇸', label: 'Español'    },
  { code: 'ja-JP', flag: '🇯🇵', label: '日本語'      },
  { code: 'zh-CN', flag: '🇨🇳', label: '中文'        },
  { code: 'pt-BR', flag: '🇧🇷', label: 'Português'  },
  { code: 'it-IT', flag: '🇮🇹', label: 'Italiano'   },
  { code: 'ko-KR', flag: '🇰🇷', label: '한국어'      },
];

const TYPE_BTN_ICONS = [
  // Order matches the PDP language table columns: Audio → Subtitles → Interface
  { key: 'a' as const, i18nKey: 'filter_lang_audio'     as const, Icon: Headphones },
  { key: 's' as const, i18nKey: 'filter_lang_subs'      as const, Icon: FileText   },
  { key: 'i' as const, i18nKey: 'filter_lang_interface' as const, Icon: Monitor    },
];

export type LangType = 'i' | 'a' | 's';
export const ALL_LANG_TYPES: LangType[] = ['i', 'a', 's'];

interface Props {
  langs: string[];
  types: LangType[];
  onChange: (langs: string[], types: LangType[]) => void;
}

export function LanguageFilter({ langs, types, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState<{ top: number; left: number } | null>(null);
  const btnRef  = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const t = useT();
  const { lang } = usePriceConvert();

  function updatePos() {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left });
    }
  }

  useEffect(() => {
    if (!open) return;
    const onMouse  = (e: MouseEvent) => {
      if (!dropRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey    = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown',   onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown',   onKey);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open]);

  function toggleLang(code: string) {
    const next = langs.includes(code) ? langs.filter(l => l !== code) : [...langs, code];
    onChange(next, types);
  }

  function toggleType(t: LangType) {
    const next = types.includes(t) ? types.filter(x => x !== t) : [...types, t];
    if (next.length === 0) return;
    onChange(langs, next);
  }

  function clear() { onChange([], ALL_LANG_TYPES); setOpen(false); }

  const isActive = langs.length > 0;

  const dropdown = open && pos ? (
    <div
      ref={dropRef}
      className="lang-drop"
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
    >
      {/* Type toggles */}
      <div className="lang-drop-types">
        {TYPE_BTN_ICONS.map(({ key, i18nKey, Icon }) => (
          <button
            key={key}
            className={`lang-type-btn${types.includes(key) ? ' active' : ''}`}
            onClick={() => toggleType(key)}
          >
            <Icon size={11} strokeWidth={2} />
            {t(i18nKey)}
          </button>
        ))}
      </div>

      <div className="lang-drop-divider" />

      {/* Language list */}
      <div className="lang-drop-list">
        {LANG_LIST.map(({ code, flag, label }) => {
          const checked = langs.includes(code);
          return (
            <button
              key={code}
              className={`lang-drop-item${checked ? ' checked' : ''}`}
              onClick={() => toggleLang(code)}
            >
              <span className="lang-drop-check">{checked && <Check size={13} strokeWidth={3} />}</span>
              <span className="lang-drop-flag">{flag}</span>
              <span className="lang-drop-label">{langName(code, lang, label)}</span>
              <span className="lang-drop-code">{code}</span>
            </button>
          );
        })}
      </div>

      {isActive && (
        <>
          <div className="lang-drop-divider" />
          <button className="lang-drop-clear" onClick={clear}>
            <X size={11} /> {t('filter_lang_reset')}
          </button>
        </>
      )}
    </div>
  ) : null;

  return (
    <div style={{ flexShrink: 0 }}>
      <button
        ref={btnRef}
        className={`va-chip${isActive ? ' on' : ''}`}
        style={{ gap: 5 }}
        onClick={() => { if (!open) updatePos(); setOpen(o => !o); }}
      >
        🌐
        <span>{t('filter_lang')}{isActive ? ` (${langs.length})` : ''}</span>
        <ChevronDown size={10} style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {typeof document !== 'undefined' && dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}

export function applyLangFilter<T extends { lang_i?: string[] | null; lang_a?: string[] | null; lang_s?: string[] | null }>(
  items: T[],
  selectedLangs: string[],
  selectedTypes: LangType[],
): T[] {
  if (selectedLangs.length === 0 || selectedTypes.length === 0) return items;
  // A game matches a language only if it supports it in EVERY selected type
  // (interface AND audio AND subtitles when all three are on). OR across languages.
  return items.filter(item =>
    selectedLangs.some(lang =>
      (!selectedTypes.includes('i') || (item.lang_i ?? []).includes(lang)) &&
      (!selectedTypes.includes('a') || (item.lang_a ?? []).includes(lang)) &&
      (!selectedTypes.includes('s') || (item.lang_s ?? []).includes(lang))
    )
  );
}
